import type { RefObject } from 'react'

import { useEffect } from 'react'

/**
 * Writes the eased pointer position to `--mx` and `--my` (each -1 to 1) on
 * the given element, so layers can shift with `calc(var(--mx) * Npx)`.
 * Skipped on touch screens and for reduced-motion visitors.
 */
export const usePointerParallax = (ref: RefObject<HTMLElement | null>, ease = 0.06): void => {
    useEffect(() => {
        const node = ref.current

        if (!node) return

        const finePointer = window.matchMedia('(pointer: fine)').matches
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        if (!finePointer || reduced) return

        let targetX = 0
        let targetY = 0
        let x = 0
        let y = 0
        let frame = 0
        let running = false

        const tick = () => {
            x += (targetX - x) * ease
            y += (targetY - y) * ease

            node.style.setProperty('--mx', x.toFixed(4))
            node.style.setProperty('--my', y.toFixed(4))

            if (Math.abs(targetX - x) > 0.0005 || Math.abs(targetY - y) > 0.0005) {
                frame = requestAnimationFrame(tick)
            }
            else {
                running = false
            }
        }

        const onMove = (event: PointerEvent) => {
            targetX = (event.clientX / window.innerWidth) * 2 - 1
            targetY = (event.clientY / window.innerHeight) * 2 - 1

            if (!running) {
                running = true
                frame = requestAnimationFrame(tick)
            }
        }

        window.addEventListener('pointermove', onMove, { passive: true })

        return () => {
            window.removeEventListener('pointermove', onMove)
            cancelAnimationFrame(frame)
        }
    }, [ref, ease])
}
